import * as _ from 'lodash';
import { Subject } from 'rxjs';
import { PaginationEvent } from './_models/pagination-event.model';
import { BaseModel } from '../../models/base/base.model';
import { SortOption } from '../collection/_models/sort-option.model';
import { CollectionUtil } from '../collection/collection.util';
import { FilterOptions } from '../collection/_models/filter-options.model';
import { FilterCase } from '../collection/_enums/filter-case.enum';
import { FilterScope } from '../collection/_enums/filter-scope.enum';

/**
 * A utility to implement pagination
 */
export class PaginatorUtil<T extends BaseModel> {
  // **********************
  // * Public properties
  // **********************

  /** An RXJS subject that will emit events when pagination properties change */
  public paginationSubject$ = new Subject<PaginationEvent>();

  /** A get accessor for the query string */
  public get query(): string {
    return this._query;
  }

  /** A get accessor for the hasFilter property */
  public get hasFilter(): boolean {
    return this._query ? true : false;
  }

  /** A get accessor for the array length */
  public get length(): number {
    return this._length;
  }

  /** A set accessor for the array length */
  public set length(length: number) {
    this._length = length;
  }

  /** A get accessor for the current page index */
  public get pageIndex(): number {
    return this._pageIndex;
  }

  /** A set accessor for the current page index */
  public set pageIndex(pageIndex: number) {
    this._pageIndex = pageIndex;
  }

  /** A get accessor for the page size */
  public get pageSize(): number {
    return this._pageSize;
  }

  /** A set accessor for the page size */
  public set pageSize(pageSize: number) {
    this._pageSize = pageSize;
  }

  /** A get accessor for the previous page index */
  public get previousPageIndex(): number {
    return this._previousPageIndex;
  }

  /** A get accessor for the page size options */
  public get pageSizeOptions(): number[] {
    return this._pageSizeOptions;
  }

  /** A get accessor for the noData property */
  public get noData(): boolean {
    return this._noData;
  }

  /** A get accessor for the sort optioins */
  public get sortOptions(): SortOption[] {
    return this._sortOptions;
  }

  // **********************
  // * Private properties
  // **********************

  /** The original data array */
  private _data: any[];

  /** The filtered data array */
  private _filteredData: any[];

  /** The length of the filtered array */
  private _length: number;

  /** The current page index */
  private _pageIndex = 0;

  /** The page size */
  private _pageSize = 5;

  /** The previous page index */
  private _previousPageIndex: number;

  /** The page size options */
  private _pageSizeOptions: number[];

  /** The no data flag */
  private _noData: boolean;

  /** The sort options */
  private _sortOptions: SortOption[];

  /** The query string */
  private _query: string;

  /** The columns to use when filtering */
  private _filterColumns: string[] = [];

  /** The filter options */
  private _filterOptions: FilterOptions = new FilterOptions();

  /**
   * Constructor
   * @param pageSize - The page size
   * @param pageSizeOptions - The page size options
   * @param sortOptions - The sort options
   */
  constructor(
    pageSize: number = 5,
    pageSizeOptions: number[] = [5, 10, 20, 50],
    ...sortOptions: SortOption[]
  ) {
    this._pageSize = pageSize;
    this._pageSizeOptions = pageSizeOptions;
    this._sortOptions = sortOptions;
  }

  // **********************
  // * Public methods
  // **********************

  /**
   * Append an object to the data array
   * @param object - The object to append
   * @param pageIndex - The page to go to after appending the object
   * @returns The modified page of data
   */
  public append(object: T, pageIndex?: number): T[] {
    if (object) {
      this._data.push(object);
      // this._length = this._data.length;
    }
    return this.paginate(pageIndex);
  }

  /**
   * Delete an object from the data array. Use the object ID to locate the object to delete.
   * @param id - The ID of the object to delete.
   * @param pageIndex - The page to go to after deleting the object
   * @returns The modified page of data
   */
  public deleteById(id: string, pageIndex?: number): T[] {
    if (id) {
      this._data = this._data.filter(f => f._id !== id);
    }
    return this.paginate(pageIndex);
  }

  /**
   * Find an object in the data array
   * @param property - The object property to use for matching
   * @param value - The property value to look for
   * @returns The matching object
   */
  public find(property: string, value: any): T {
    const result = CollectionUtil.find(this._data, property, value);
    return result;
  }

  /**
   * Find an object in the data array by matching the object ID
   * @param id - The ID of the object to find
   * @returns The matching object
   */
  public getById(id: string): T {
    const object = CollectionUtil.findById(this._data, id);
    return object;
  }

  /**
   * Go to the start of the data array
   */
  public goFirst() {
    this._pageIndex = 0;
    return this.paginate();
  }

  /** Go to the end of the data array */
  public goLast() {
    const pageCount = Math.floor(this._length / this._pageSize);
    this._pageIndex = pageCount - 1;
    if (this._pageIndex < 0) {
      this._pageIndex = 0;
    }
  }

  /**
   * Insert an object in the data array
   * @param object - The object to insert
   * @param pageIndex - The page to go to after inserting the object
   * @returns The modified page of data
   */
  public insert(object: T, pageIndex?: number): T[] {
    if (object) {
      this._data.unshift(object);
    }
    return this.paginate(pageIndex);
  }

  /**
   * Load the passed data into the data array
   * @param data - The data to load
   * @returns The first page of data
   */
  public loadData(data: T[]): T[] {
    this._data = data;
    this._length = data.length;
    this._noData = data.length === 0 ? true : false;
    const filteredData = this.paginate();
    return filteredData;
  }

  /**
   * Update the paginator properties and trigger a pagination event
   * @param pageData - The paginator properties to update
   */
  public async onPagination(pageData: PaginationEvent) {
    pageData.pageSizeChanged = this._pageSize !== pageData.pageSize;
    this._length = pageData.length;
    this._pageIndex = pageData.pageIndex;
    this._pageSize = pageData.pageSize;
    this._previousPageIndex = pageData.previousPageIndex;
    this.paginationSubject$.next(pageData);
  }

  /**
   * Trigger a pagination event
   * @param pageIndex - The page to go to after re-sorting and filtering
   * @Returns The current page of data
   */
  public paginate(pageIndex?: number): T[] {
    this.sort();
    this._filteredData = this.filter();
    this._length = this._filteredData.length;
    if (pageIndex !== undefined) {
      this._pageIndex = pageIndex;
    }
    let start = this._pageIndex * this._pageSize;
    if (start >= this.length) {
      this._pageIndex--;
      if (this.pageIndex < 0) {
        this.pageIndex = 0;
      }
      start = this._pageIndex * this._pageSize;
    }
    const end = start + this._pageSize;
    const dataToReturn = _.slice(this._filteredData, start, end);
    return dataToReturn;
  }

  /**
   * Set the sort options
   * @param sortOptions - The sort options
   */
  public setSortOptions(...sortOptions: SortOption[]) {
    this._sortOptions = sortOptions;
  }

  /**
   * Set the filter columns
   * @param filterColumns - The filter columns
   */
  public setFilterColumns(...filterColumns: string[]): void {
    this._filterColumns = filterColumns;
  }

  /**
   * Set the filter options
   * @param fitlerCase - The filter case option
   * @param fitlerScope - The filter scope option
   */
  public setFilterOptions(
    fitlerCase: FilterCase,
    fitlerScope: FilterScope
  ): void {
    this._filterOptions = new FilterOptions(fitlerCase, fitlerScope);
  }

  /**
   * Set the filter query
   * @param query - The filter query
   * @returns A page of data
   */
  public setQuery(query: string): T[] {
    this._query = query;
    return this.paginate();
  }

  /**
   * Update an object in the data array
   * @param object - The object to update
   * @param pageIndex - The page to go to after updating
   * @Returns The current page of data
   */
  public update(object: T, pageIndex?: number): T[] {
    const index = this._data.findIndex(o => o._id === object._id);
    if (index < 0) {
      this._data.push(object);
      // this._length = this._data.length;
    } else {
      this._data[index] = object;
    }
    return this.paginate(pageIndex);
  }

  /**
   * Apply the current filter
   */
  private filter(): T[] {
    if (!this._query || !this._data) {
      return this._data;
    }
    const filteredData = CollectionUtil.filter(
      this._data,
      this._query,
      this._filterColumns,
      this._filterOptions
    );
    return filteredData;
  }

  /**
   * Apply the current sort options
   */
  private sort(): void {
    if (!this._sortOptions) {
      return;
    }
    if (!this._data) {
      return;
    }
    CollectionUtil.multiKeySort(this._data, ...this._sortOptions);
  }
}
